import type { ReactNode } from "react";
import {
  Badge,
  fmtLongDate,
  type PortalDashboard,
  type PortalSignableDocument,
} from "@dearbill/shared";
import type { BadgeTone } from "@dearbill/shared/components/Badge";
import { signedByAnother } from "../sign/documents";
import { HomeCard } from "./HomeCard";

const LINK = "text-primary-text text-body underline hover:no-underline";

const BADGE: Record<"signed" | "unsigned", { tone: BadgeTone; label: string }> = {
  signed: { tone: "active", label: "Signed" },
  unsigned: { tone: "pending", label: "Not signed" },
};

function badgeFor(doc: PortalSignableDocument): { tone: BadgeTone; label: string } {
  if (doc.status === "signed") return BADGE.signed;
  return BADGE.unsigned;
}

/** Who may sign a document only someone other than this session may sign. */
function memberOnlyLine(doc: PortalSignableDocument): string {
  return `Only ${doc.signerName || "the member"} can sign this authorization.`;
}

/** The status line under a document's title. */
function statusLine(doc: PortalSignableDocument, role: PortalDashboard["role"]): string {
  if (doc.status === "signed") {
    return doc.signedAt ? `Signed on ${fmtLongDate(doc.signedAt)}.` : "Signed.";
  }
  if (doc.signable) return "Waiting for your signature. Sign it from Action Needed above.";
  if (signedByAnother(doc, role)) return memberOnlyLine(doc);
  return "Not ready to sign yet.";
}

interface DocumentsCardProps {
  documents: PortalSignableDocument[];
  /** The session's role, which decides whether an unsignable document names its signer. */
  role: PortalDashboard["role"];
}

/**
 * Home's list of the authorizations DearBill asks the member to sign, with
 * each one's status and, once signed, its signed copy. A signature starts
 * from the Action Needed panel.
 */
export function DocumentsCard({ documents, role }: DocumentsCardProps): ReactNode {
  return (
    <HomeCard title="Your authorizations" titleSize="lg">
      <ul className="flex flex-col gap-4">
        {documents.map((doc) => {
          const badge = badgeFor(doc);
          const hasCopy = doc.status === "signed" && (doc.signedDocUrl || doc.signedDocDownloadUrl);
          return (
            <li
              key={doc.documentId}
              className="border-haze flex flex-col gap-2 border-t pt-4 first:border-t-0 first:pt-0"
            >
              <div className="flex items-start justify-between gap-4">
                <span className="text-body text-midnight min-w-0 font-semibold">{doc.title}</span>
                <Badge tone={badge.tone} className="shrink-0">
                  {badge.label}
                </Badge>
              </div>
              <p className="text-body text-slate-teal">{statusLine(doc, role)}</p>
              {hasCopy && (
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {doc.signedDocUrl && (
                    <a
                      href={doc.signedDocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={LINK}
                    >
                      Open your signed copy
                    </a>
                  )}
                  {doc.signedDocDownloadUrl && (
                    <a href={doc.signedDocDownloadUrl} className={LINK}>
                      Download your signed copy
                    </a>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </HomeCard>
  );
}
